/**
 * Belegarchiv -- der ausgestellte Beleg, so wie er ausgestellt wurde.
 *
 * BEFUND (09/2026): FORQ speicherte weder ausgehende Belege noch eingehende Originale. Eine
 * Rechnung wurde bei JEDEM Aufruf live neu gerendert -- aus dem AKTUELLEN Firmenprofil. Änderte
 * sich der Briefkopf, die Anschrift oder ein Belegtext, sah eine zwei Jahre alte Rechnung beim
 * Nachdruck anders aus als beim Versand. Das trifft § 14b UStG (Aufbewahrung im ursprünglichen
 * Zustand) und die GoBD (Unveränderbarkeit, Nachvollziehbarkeit) gleichermaßen.
 *
 * Die Ablage ist bewusst SCHREIB-EINMALIG: Zu einer Belegnummer gibt es genau einen Eintrag; ein
 * zweiter Versuch wird abgewiesen, geändert oder gelöscht werden kann er nicht. Die Prüfsumme über
 * den Inhalt macht eine spätere Manipulation an der Datenbank erkennbar.
 */

/** Welche Belegart archiviert wurde -- bewusst ein loser String, damit neue Arten keine Migration brauchen. */
export type ArchivedDocumentKind = 'invoice' | 'credit_note';

export interface ArchivedDocument {
  readonly id: string;
  readonly tenantId: string;
  readonly kind: ArchivedDocumentKind;
  /** Die gesetzliche Belegnummer (RE-26-…, GS-26-…) -- der Schlüssel des Archivs. */
  readonly documentNumber: string;
  /** Der Beleg selbst als druckfertiges HTML, exakt wie ausgestellt. */
  readonly contentHtml: string;
  /** SHA-256 über contentHtml (hex) -- macht eine Veränderung erkennbar. */
  readonly checksum: string;
  /** Die Eingangsdaten des Belegs (Rechnung, Firmenprofil, Empfänger) zur Nachvollziehbarkeit. */
  readonly snapshot: Readonly<Record<string, unknown>>;
  readonly archivedAt: string;
  readonly archivedBy: string;
}

export interface ArchiveDocumentInput {
  readonly tenantId: string;
  readonly kind: ArchivedDocumentKind;
  readonly documentNumber: string;
  readonly contentHtml: string;
  /** SHA-256 über contentHtml -- vom Aufrufer mit `documentChecksum` gebildet. */
  readonly checksum: string;
  readonly snapshot: Readonly<Record<string, unknown>>;
}

export interface DocumentArchiveGateway {
  /**
   * Legt den Beleg ab. Existiert zu dieser Nummer schon ein Eintrag, wird der VORHANDENE
   * zurückgegeben statt ihn zu überschreiben -- damit ist ein Wiederholen gefahrlos und das
   * Archiv bleibt unveränderlich.
   */
  archiveDocument(input: ArchiveDocumentInput): Promise<ArchivedDocument>;
  /** Der archivierte Beleg zu einer Nummer, oder null, wenn (noch) keiner abgelegt ist. */
  getArchivedDocument(tenantId: string, documentNumber: string): Promise<ArchivedDocument | null>;
}

/**
 * SHA-256 über den Belegtext, hex. Nutzt die Web-Crypto-API des Browsers; steht sie nicht zur
 * Verfügung (sehr alte Umgebung, Test ohne Crypto), wird bewusst ein leerer String geliefert --
 * dann fehlt die Prüfsumme sichtbar, statt eine wertlose zu erfinden.
 */
export async function documentChecksum(content: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return '';
  const bytes = new TextEncoder().encode(content);
  const digest = await subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
